import stringify from 'fast-json-stable-stringify';
import { Resolver } from '../resolvers/definition/resolver';
import { fromLinksOnly } from '../resolvers/adapters/fromLinksOnly';
import { ResourceRegistry, defaultResourceRegistry } from './resourceRegistry';
import {
  LinkBuilder,
  LinkBuilderInitial,
  LinkBuilderFinalizable,
  type SelfLinkBuilder,
} from '../links/definition/linkBuilder';
import {
  AnyResourceDefinition,
  ArgEntry,
  CoreResourceEgg,
  HashConfig,
  ResourceDefinition,
  ResourceDefinitionEgg,
  RejectUndefinedData,
  EQUAL_HASH_MODE,
} from './types';
import { Link } from '../links/definition/link';
import { Resource, createResourceMethods } from './resourceMethods';

export const SELF: unique symbol = Symbol('pulse:self-link');
export type SelfLink = typeof SELF;

export const getVersionedTypeName = (typeName: string, schemaVersion = '1') =>
  `${typeName}@${schemaVersion}`;

/**
 * Produce an instance key that uniquely identifies an instance.
 *
 * Uses length-prefixed encoding to avoid problems with folks using
 * our delimiter in their typeName/instance information.
 */
export const createInstanceKey = (
  typeName: string,
  instanceIdentifier: string
) => `${typeName.length}:${typeName}:${instanceIdentifier}`;

const getTypeNameIndex = (key: string) => {
  const colonIndex = key.indexOf(':');

  if (colonIndex === -1) {
    throw Error(`Invalid instance key: ${key}`);
  }

  const typeNameLength = parseInt(key.substring(0, colonIndex), 10);

  // typeName starts after the first colon
  const typeNameStart = colonIndex + 1;
  const typeNameEnd = typeNameStart + typeNameLength;

  return { typeNameStart, typeNameEnd };
};

/**
 * Parse the typeName out of an instance key
 */
export const parseTypeName = (key: string) => {
  const { typeNameStart, typeNameEnd } = getTypeNameIndex(key);

  return key.substring(typeNameStart, typeNameEnd);
};

export const parseInstanceKey = (key: string) => {
  const { typeNameStart, typeNameEnd } = getTypeNameIndex(key);

  return {
    typeName: key.substring(typeNameStart, typeNameEnd),
    identifier: key.substring(typeNameEnd + 1),
  };
};

const argField = <TParams>(arg: ArgEntry<TParams>) =>
  typeof arg === 'string' ? arg : arg.field;

/**
 * Append the resolver's own identity args to the resource's. A resolver
 * declares a field because it *reads* that param, so its entry is
 * authoritative: any resource entry for the same field is dropped. This
 * matters for endpoint-routing fields (env/hublet/impersonation) — a
 * resource-supplied `{ field, default }` would move the cache key without
 * changing what the resolver actually sends, letting an omitted value collide
 * with an explicit one that fetched a different endpoint.
 */
const mergeResolverArgs = <TParams>(
  eggArgs: Array<ArgEntry<TParams>> | undefined,
  resolverArgs: Array<ArgEntry<TParams>> | undefined
) => {
  if (!resolverArgs || resolverArgs.length === 0) {
    return eggArgs;
  }

  const reserved = new Set(resolverArgs.map(argField));

  return [
    ...(eggArgs ?? []).filter((arg) => !reserved.has(argField(arg))),
    ...resolverArgs,
  ];
};

export const applyDefaultsToEgg = <
  TParams,
  TData,
  TError extends Error = Error,
>(
  egg: ResourceDefinitionEgg<TParams, TData, TError>
): Resource<TParams, TData, TError> => {
  const versionedTypeName = getVersionedTypeName(
    egg.typeName,
    egg.schemaVersion
  );
  const instanceKeyPrefix = `${versionedTypeName.length}:${versionedTypeName}:`;
  const resolveArg = (arg: ArgEntry<TParams>, params: TParams): string => {
    const record = params as Record<string, unknown>;
    if (typeof arg === 'string') {
      const v = record[arg];
      return `${arg}:${typeof v === 'object' && v !== null ? stringify(v) : v}`;
    }
    const v = record[arg.field] ?? arg.default;
    return `${arg.field as string}:${
      typeof v === 'object' && v !== null ? stringify(v) : v
    }`;
  };

  const args = mergeResolverArgs(egg.args, egg.resolver?.args);

  const deriveIdentifier = (params: TParams): string => {
    if (args && args.length) {
      let result = resolveArg(args[0], params);
      for (let i = 1; i < args.length; i++) {
        // eslint-disable-next-line prefer-template
        result += ',' + resolveArg(args[i], params);
      }
      return result;
    }
    return '';
  };

  const definition = {
    ...egg,
    args,
    typeName: versionedTypeName,
    resolver: egg.resolver!,
    identifyInstance: (params: TParams) =>
      instanceKeyPrefix + deriveIdentifier(params),
    hash: {
      mode: EQUAL_HASH_MODE,
      create: (data: TData) => data,
      ...egg.hash,
    },
  } as ResourceDefinition<TParams, TData, TError>;

  const resource = Object.assign(definition, createResourceMethods(definition));

  return Object.freeze(resource) as Resource<TParams, TData, TError>;
};

/**
 * Create a basic resource definition. Must be statically analyzable (i.e called in module scope). This will be enforced.
 *
 * Automatically registers the resource with the global registry.
 *
 * @param typeName - A string that uniquely identifies the resource's type
 * @param schemaVersion - The version of the schema for this resource. Defaults to '1'.
 * @param resolver - The resolver used for this resource.
 * @param args - An array of param keys that identify a unique instance. Entries can be bare keys (`'userId'`) or `{ field, default }` objects for optional params with semantic defaults. Required unless params are empty.
 * @param hash.create - A function that produces a hash for a resource's data used for change detection (defaults to returning the data for a reference check)
 * @param hash.mode - The type of comparison to use when comparing hashes. Defaults to EQUAL (===). Use GREATER to compare timestamps or version numbers (requires create to return a number)
 * @param onChange - A function that is called when the resource's state changes. Used when updates need to be pushed into other systems on change.
 *
 * @example
 * ```ts
 * // Define a "User" resource derived from a promise-based fetcher
 *
 * const fetchUser = (httpClient, { userId }: { userId: number }) =>
 *   httpClient.get<User>(`users/v1/users/${userId}`);
 *
 * const userResolver = fromPromise('fetchUser', fetchUser);
 *
 * const User = defineResource({
 *   typeName: 'User',
 *   resolver: userResolver,
 *   args: ['userId'],
 *   hash: {
 *     create: stableStringify // for a deep structural diff (useful for small objects)
 *   }
 * }).register();
 *
 * const Team = defineResource({
 *   typeName: 'Team',
 *   resolver: teamResolver,
 *   args: ['teamId'],
 *   hash: {
 *     // Check that new timestamp is greater than old
 *     mode: 'GREATER',
 *     create: data => data.updatedAt
 *   }
 * }).register();
 *
 * // pass to useResource in React
 * const {
 *   data: user,
 *   loading,
 *   error,
 *   stale,
 * } = useResource(User, { params: { userId: 123 } });
 * ```
 */
type OnChangeHandler<
  TParams,
  TData,
  TError extends Error = Error,
> = NonNullable<ResourceDefinition<TParams, TData, TError>['onChange']>;

export type LinkResult<TParams, TData, TError extends Error = Error> = {
  register: (registry?: ResourceRegistry) => Resource<TParams, TData, TError>;
  linkTo: {
    (
      target: SelfLink,
      construct: (
        builder: SelfLinkBuilder<TParams, TData>
      ) => SelfLinkBuilder<TParams, TData, any, any>
    ): LinkResult<TParams, TData, TError>;
    <TTargetParams, TTargetData, TTargetError extends Error = Error>(
      target: ResourceDefinition<TTargetParams, TTargetData, TTargetError>,
      construct: (
        builder: LinkBuilderInitial<TParams, TData, TTargetParams, TTargetData>
      ) => LinkBuilderFinalizable<TParams, TData, TTargetParams, TTargetData>
    ): LinkResult<TParams, TData, TError>;
  };
};

export type DefineResourceResult<
  TParams,
  TData,
  TError extends Error = Error,
> = LinkResult<TParams, TData, TError> & {
  onChange: (
    fn: OnChangeHandler<TParams, TData, TError>
  ) => LinkResult<TParams, TData, TError>;
};

type ResolverEgg<TParams, TData, TError extends Error = Error> = Omit<
  ResourceDefinition<TParams, TData, TError>,
  'identifyInstance' | 'hash'
> & {
  hash?: HashConfig<TData>;
};

const buildResource = <TParams, TData, TError extends Error = Error>({
  typeName,
  schemaVersion,
  resolver,
  args,
  hash,
  persist,
  peerSyncMaxAge,
  staleTime,
  retryOnMount,
  keepAliveWhenHidden,
}: ResolverEgg<TParams, TData, TError>): DefineResourceResult<
  TParams,
  TData,
  TError
> => {
  let onChangeHandler: OnChangeHandler<TParams, TData, TError> | undefined;

  const links: Link<any, any, any, any>[] = [];

  const resource = applyDefaultsToEgg({
    typeName,
    schemaVersion,
    resolver,
    args,
    hash,
    persist,
    peerSyncMaxAge,
    staleTime,
    retryOnMount,
    keepAliveWhenHidden,
  } as unknown as ResourceDefinitionEgg<TParams, TData, TError>);

  const getResourceWithOnChange = (): Resource<TParams, TData, TError> => {
    if (!onChangeHandler) {
      return resource;
    }

    const withOnChange = { ...resource, onChange: onChangeHandler };

    return Object.freeze(
      Object.assign(withOnChange, createResourceMethods(withOnChange))
    ) as Resource<TParams, TData, TError>;
  };

  const register = (
    __targetRegistry: ResourceRegistry = defaultResourceRegistry
  ) => {
    const fullResource = getResourceWithOnChange();

    __targetRegistry.registerResource(fullResource, links);

    return fullResource;
  };

  const linksResult: LinkResult<TParams, TData, TError> = {
    register,
    linkTo: (
      target: AnyResourceDefinition | SelfLink,
      construct: (builder: LinkBuilder<any, any, any, any>) => {
        finalize(): Link<any, any, any, any>;
      }
    ) => {
      const resolvedTarget = target === SELF ? resource : target;

      if (!resolvedTarget) {
        throw new Error(
          'Target is undefined. Please check for an import cycle'
        );
      }

      links.push(
        construct(new LinkBuilder(resource, resolvedTarget)).finalize()
      );

      return linksResult;
    },
  };

  return {
    onChange: (fn: OnChangeHandler<TParams, TData, TError>) => {
      onChangeHandler = fn;

      return linksResult;
    },
    ...linksResult,
  };
};

export const defineResource = <TParams, TData, TError extends Error = Error>(
  egg: ResourceDefinitionEgg<TParams, TData, TError> & {
    resolver: Resolver<TParams, TData, TError>;
  }
): RejectUndefinedData<TData, DefineResourceResult<TParams, TData, TError>> =>
  buildResource(egg) as RejectUndefinedData<
    TData,
    DefineResourceResult<TParams, TData, TError>
  >;

/**
 * Define a resource with no transport of its own, populated only via links from
 * another resource (the `fromLinksOnly` resolver is wired in for you).
 *
 * Use it for a by-id resource when there is no by-id endpoint — a bulk query
 * fans out into per-id entries via a `.push()` / `.pull()` link. A cold read (no
 * pushed data) errors loudly with {@link LINK_ONLY_NO_FETCH_MESSAGE}, which
 * consumers must treat as "not in cache yet"; any refresh / invalidate / poll
 * also errors, since there is nothing to refetch. See the "Client-side
 * normalization" recipe.
 *
 * Takes the same options as `defineResource` except `resolver`, and returns the
 * same chainable builder. Persistence config is intentionally excluded — it is
 * meaningless on a virtual, frontend-only resource.
 *
 * @example
 * ```ts
 * const CalendarEvent = defineLinkOnlyResource<{ id: string }, CalendarEventData>({
 *   typeName: 'calendar:CalendarEvent',
 *   args: ['id'],
 * })
 *   .linkTo(CalendarEventsByDateRange, (b) =>
 *     b
 *       .findSelf({ fromOtherData: ({ data }) => data.map(({ id }) => ({ id })) })
 *       .pull(({ self, other }) => other.data.find(({ id }) => id === self.params.id))
 *   )
 *   .register();
 * ```
 */
export const defineLinkOnlyResource = <TParams, TData>(
  egg: CoreResourceEgg<TParams, TData> & { resolver?: never }
): RejectUndefinedData<TData, DefineResourceResult<TParams, TData>> =>
  buildResource({
    ...egg,
    resolver: fromLinksOnly<TData, TParams>(egg.typeName) as Resolver<
      TParams,
      TData
    >,
  }) as RejectUndefinedData<TData, DefineResourceResult<TParams, TData>>;
